import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CheckCircle2, ChevronLeft, ChevronRight, ShieldCheck, Save, Copy, Share2, Printer, UserPlus, FileText, ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, shareOrCopy, copyLinkOnly, photoSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavGuard } from "../context/NavGuardContext";
import { VENEZUELA_STATES, SEX_OPTIONS, CONTACT_METHODS, PLACE_TYPES, REPORT_REASONS } from "../lib/constants";
import { Input, Textarea, Check, DateField } from "./ContactForm";
import { PhotoUploader } from "../components/PhotoUploader";
import { addPending } from "../lib/offlineQueue";

function computeAge(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d)) return "";
  const diff = Date.now() - d.getTime();
  const age = Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  return age >= 0 && age < 130 ? String(age) : "";
}

const emptyForm = {
  person: { first_name: "", last_name: "", nickname: "", approximate_age: "", date_of_birth: "", sex: "",
    nationality: "", venezuelan_id_number: "", is_minor: false, physical_description: "",
    clothing_description: "", distinguishing_marks: "", medical_notes_private: "" },
  location: { country: "Venezuela", state_region: "", municipality: "", parish_zone: "",
    approximate_area_text: "", exact_address_private: "", location_type: "" },
  reporter: { first_name: "", last_name: "", phone: "", email: "", preferred_contact_method: "phone",
    report_reason: "", relationship_to_person: "", message_private: "" },
  primary_photo_id: null,
  approximate_date: "",
  photo_ok: { corresponds: false, respectful: false, not_graphic: false, not_document: false, not_deceased: false },
  good_faith_confirmation: false, terms_accepted: false, privacy_accepted: false,
};

export default function ReportForm({ caseType }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setGuard, attempt } = useNavGuard();
  const draftKey = `reencuentro_draft_${caseType}`;
  const [step, setStep] = useState(1);
  const [hadDraft, setHadDraft] = useState(() => {
    try { return !!localStorage.getItem(draftKey); } catch { return false; }
  });
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(draftKey);
      return saved ? { ...emptyForm, ...JSON.parse(saved) } : emptyForm;
    } catch { return emptyForm; }
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [dupMatches, setDupMatches] = useState(null);
  const [savedOffline, setSavedOffline] = useState(false);
  const isMissing = caseType === "missing";
  const totalSteps = 5;

  const dirty = !result && JSON.stringify(form) !== JSON.stringify(emptyForm);

  useEffect(() => {
    const t = setTimeout(() => {
      if (JSON.stringify(form) !== JSON.stringify(emptyForm)) localStorage.setItem(draftKey, JSON.stringify(form));
      else localStorage.removeItem(draftKey);
    }, 400);
    return () => clearTimeout(t);
  }, [form, draftKey]);

  useEffect(() => {
    setGuard({
      dirty,
      onSaveDraft: () => localStorage.setItem(draftKey, JSON.stringify(form)),
      onDiscard: () => localStorage.removeItem(draftKey),
    });
    return () => setGuard(null);
  }, [dirty, form, draftKey, setGuard]);

  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const setPerson = (k, v) => setForm({ ...form, person: { ...form.person, [k]: v } });
  const setLoc = (k, v) => setForm({ ...form, location: { ...form.location, [k]: v } });
  const setRep = (k, v) => setForm({ ...form, reporter: { ...form.reporter, [k]: v } });
  const setPhotoOk = (k, v) => setForm({ ...form, photo_ok: { ...form.photo_ok, [k]: v } });

  const photoConfirmKeys = isMissing
    ? ["corresponds", "respectful", "not_graphic", "not_document", "not_deceased"]
    : ["respectful", "not_graphic", "not_deceased"];
  const photoConfirmComplete = photoConfirmKeys.every((k) => form.photo_ok[k]);

  const next = () => {
    setError("");
    if (step === 2 && form.primary_photo_id && !photoConfirmComplete) {
      setError("Para publicar una foto debes marcar todas las confirmaciones, o quitar la foto y continuar sin ella.");
      return;
    }
    setStep((s) => Math.min(s + 1, totalSteps));
    window.scrollTo(0, 0);
  };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 1)); window.scrollTo(0, 0); };

  const handleVolver = () => {
    if (step > 1) { back(); return; }
    attempt(() => navigate("/"));
  };

  const discardDraft = () => {
    localStorage.removeItem(draftKey);
    setForm(emptyForm);
    setHadDraft(false);
  };

  const submit = async () => {
    setError("");
    if (!form.good_faith_confirmation || !form.terms_accepted || !form.privacy_accepted) {
      setError("Debes aceptar la buena fe, los términos y la privacidad."); return;
    }
    if (!form.reporter.phone && !form.reporter.email) {
      setError("Debes proporcionar teléfono o email de contacto."); return;
    }
    // Offline: save locally to the queue instead of submitting
    if (!navigator.onLine) {
      await saveOffline();
      return;
    }
    setSubmitting(true);
    try {
      const { data: dup } = await api.post("/cases/check-duplicates", {
        case_type: caseType,
        person: { ...form.person, approximate_age: form.person.approximate_age ? Number(form.person.approximate_age) : null },
        location: form.location,
        approximate_date: form.approximate_date,
      });
      if (dup.matches && dup.matches.length) { setDupMatches(dup.matches); setSubmitting(false); return; }
    } catch (e) { /* if check fails, continue to create */ }
    await runCreate(null);
  };

  const saveOffline = async () => {
    await addPending({
      id: (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()),
      case_type: caseType, form, created_at: new Date().toISOString(), status: "pending",
    });
    localStorage.removeItem(draftKey);
    setSavedOffline(true);
    window.scrollTo(0, 0);
  };

  const runCreate = async (dupInfo) => {
    setSubmitting(true);
    try {
      const payload = {
        case_type: caseType,
        person: { ...form.person, approximate_age: form.person.approximate_age ? Number(form.person.approximate_age) : null },
        location: form.location,
        reporter: form.reporter,
        primary_photo_id: form.primary_photo_id,
        approximate_date: form.approximate_date,
        good_faith_confirmation: form.good_faith_confirmation,
        terms_accepted: form.terms_accepted,
        privacy_accepted: form.privacy_accepted,
        photo_confirmations: { is_graphic: false, contains_document: false, is_deceased: false },
        possible_duplicate_of: dupInfo?.id || null,
        duplicate_match_reason: dupInfo?.match_reason || null,
        duplicate_match_level: dupInfo?.match_level || null,
      };
      const { data } = await api.post("/cases", payload);
      localStorage.removeItem(draftKey);
      setDupMatches(null);
      setResult(data);
    } catch (e) {
      if (!e.response) { await saveOffline(); setDupMatches(null); return; }
      setError(formatApiError(e.response?.data?.detail));
      setDupMatches(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (savedOffline) {
    return (
      <div className="max-w-lg mx-auto py-8 space-y-5 text-center" data-testid="report-saved-offline">
        <Save className="w-14 h-14 text-[#003893] mx-auto" />
        <h1 className="font-heading text-2xl font-bold">Reporte guardado en este dispositivo</h1>
        <p className="text-sm text-[#475569] bg-[#F1F5F9] rounded-xl p-4 leading-relaxed">
          Aún no ha sido enviado. Se enviará cuando haya conexión. Podrás revisarlo y enviarlo desde "Reportes pendientes".
        </p>
        <Link to="/pendientes" data-testid="saved-offline-pending-btn"
          className="block text-center py-3 rounded-xl bg-[#003893] text-white font-semibold">Ver reportes pendientes</Link>
        <Link to="/" className="block text-center text-sm text-[#475569] underline">Volver al inicio</Link>
      </div>
    );
  }

  if (result) {
    const caseUrl = `${window.location.origin}/caso/${result.public_case_code}`;
    const copyLink = () => copyLinkOnly(caseUrl, () => toast.success("Enlace copiado"));
    const share = async () => {
      const r = await shareOrCopy(caseUrl, "Reencuentro", `Ficha pública: ${result.public_case_code}`, () => toast.success("Enlace copiado"));
      if (r === "failed") toast.error("No se pudo compartir el enlace");
    };
    return (
      <div className="max-w-lg mx-auto py-8 space-y-5" data-testid="report-success">
        <div className="text-center space-y-3">
          <CheckCircle2 className="w-14 h-14 text-[#047857] mx-auto" />
          <h1 className="font-heading text-2xl font-bold">Reporte recibido</h1>
          <div>
            <p className="text-sm text-[#475569]">Código del caso</p>
            <p className="font-mono text-2xl font-bold text-[#1E3A8A]" data-testid="report-code">{result.public_case_code}</p>
            <p className="text-sm text-[#475569] mt-1">Estado inicial: <b>{result.status_label || result.status}</b></p>
          </div>
        </div>

        <p className="text-sm text-[#475569] bg-[#F1F5F9] rounded-xl p-4 leading-relaxed">
          Tu reporte fue recibido. Si el caso es de bajo riesgo, se publicará como ficha limitada pendiente de verificación.
          Si involucra a un menor, datos sensibles o posible riesgo, será enviado a revisión. Tus datos de contacto no serán públicos.
        </p>
        {result.review_required && !result.public_visibility && (
          <p className="text-sm text-[#9A3412] bg-[#FFEDD5] rounded-lg p-3" data-testid="report-review-note">
            Este reporte fue recibido, pero requiere revisión antes de mostrarse públicamente.
          </p>
        )}
        {result.is_minor_case && <p className="text-sm text-[#9A3412] bg-[#FFEDD5] rounded-lg p-3">Por tratarse de un menor, este caso tiene protección especial y revisión prioritaria.</p>}

        {result.management_code && (
          <div className="rounded-xl border-2 border-[#FFD100] bg-[#FFFBEB] p-4 space-y-2" data-testid="report-management-code">
            <p className="text-sm font-semibold text-[#7A5E00]">Código privado de gestión</p>
            <div className="flex items-center gap-2">
              <code className="text-lg font-mono font-bold text-[#0F172A] tracking-wider" data-testid="management-code-value">{result.management_code}</code>
              <button onClick={() => { navigator.clipboard?.writeText(result.management_code); toast.success("Código copiado"); }}
                data-testid="copy-management-code" className="h-8 px-2 rounded-lg bg-white border border-[#E5E7EB] text-xs font-medium">Copiar</button>
            </div>
            <p className="text-xs text-[#8A6D00] leading-relaxed">
              Guarda este código privado. Lo necesitarás para revisar solicitudes, respuestas o información relacionada con tu reporte en <b>“Consultar mi reporte”</b>. No lo compartas públicamente.
            </p>
            {!user && (
              <p className="text-xs text-[#991B1B]">Si pierdes este código y no tienes cuenta, podrías no volver a acceder al seguimiento de este reporte.</p>
            )}
            <Link to="/consultar-reporte" data-testid="report-lookup-link" className="inline-block text-sm text-[#003893] font-semibold underline">Consultar mi reporte</Link>
          </div>
        )}

        <Link to={`/caso/${result.public_case_code}`} data-testid="report-view-btn"
          className="block text-center h-13 py-3 rounded-xl bg-[#1E3A8A] text-white font-semibold">Ver ficha pública limitada</Link>

        <div className="grid grid-cols-3 gap-3">
          <button onClick={copyLink} data-testid="report-copy-btn" className="flex flex-col items-center gap-1 py-3 rounded-xl bg-[#F1F5F9] text-sm font-medium text-[#0F172A]"><Copy className="w-5 h-5" /> Copiar enlace</button>
          <button onClick={share} data-testid="report-share-btn" className="flex flex-col items-center gap-1 py-3 rounded-xl bg-[#F1F5F9] text-sm font-medium text-[#0F172A]"><Share2 className="w-5 h-5" /> Compartir</button>
          <Link to={`/caso/${result.public_case_code}/imprimir`} data-testid="report-print-btn" className="flex flex-col items-center gap-1 py-3 rounded-xl bg-[#F1F5F9] text-sm font-medium text-[#0F172A]"><Printer className="w-5 h-5" /> Imprimir</Link>
        </div>

        {user ? (
          <Link to="/mis-reportes" data-testid="report-myreports-btn"
            className="flex items-center justify-center gap-2 h-13 py-3 rounded-xl bg-white border border-[#E2E8F0] font-semibold text-[#0F172A]">
            <FileText className="w-5 h-5" /> Ver mis reportes
          </Link>
        ) : (
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-4 space-y-2">
            <Link to="/registro" data-testid="report-createaccount-btn"
              className="flex items-center justify-center gap-2 h-12 rounded-xl bg-white border border-[#1E3A8A] text-[#1E3A8A] font-semibold">
              <UserPlus className="w-5 h-5" /> Crear cuenta para gestionar este reporte
            </Link>
            <p className="text-xs text-[#94A3B8] text-center">Para editar, cerrar o gestionar tus reportes necesitas iniciar sesión.</p>
          </div>
        )}
        <Link to="/" className="block text-center text-sm text-[#475569] underline">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-5">
      <button onClick={handleVolver} data-testid="report-volver-btn"
        className="flex items-center gap-1.5 text-sm font-medium text-[#475569] hover:text-[#1E3A8A]">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      {hadDraft && step === 1 && (
        <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-3 flex items-center justify-between gap-3" data-testid="draft-banner">
          <p className="text-sm text-[#1E40AF] font-medium">Tienes un borrador guardado.</p>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setHadDraft(false)} data-testid="draft-continue-btn"
              className="h-9 px-3 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium">Continuar borrador</button>
            <button onClick={discardDraft} data-testid="draft-discard-btn"
              className="h-9 px-3 rounded-lg bg-white border border-[#FCA5A5] text-[#991B1B] text-sm font-medium">Descartar borrador</button>
          </div>
        </div>
      )}
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#0F172A]">
          {isMissing ? "Reportar persona desaparecida" : "Reportar persona encontrada"}
        </h1>
        <div className="flex items-center gap-2 mt-3">
          {[...Array(totalSteps)].map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i < step ? "bg-[#1E3A8A]" : "bg-[#E2E8F0]"}`} />
          ))}
        </div>
        <p className="text-xs text-[#94A3B8] mt-1 flex items-center gap-1"><Save className="w-3 h-3" /> Paso {step} de {totalSteps} · borrador guardado automáticamente</p>
      </div>

      {step === 1 && (
        <div className="space-y-4" data-testid="report-step-1">
          <h2 className="font-heading text-lg font-semibold">{isMissing ? "Datos de la persona" : "Datos observados"}</h2>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Nombre" testid="rf-firstname" value={form.person.first_name} onChange={(v) => setPerson("first_name", v)} />
            <Input label="Apellido" testid="rf-lastname" value={form.person.last_name} onChange={(v) => setPerson("last_name", v)} />
          </div>
          <Input label="Apodo" testid="rf-nickname" value={form.person.nickname} onChange={(v) => setPerson("nickname", v)} />
          <DateField label="Fecha de nacimiento (opcional, privada)" testid="rf-dob" max={new Date().toISOString().slice(0, 10)}
            hint="Si la indicas, calcularemos la edad aproximada automáticamente. No se publica."
            value={form.person.date_of_birth}
            onChange={(v) => {
              const age = computeAge(v);
              const upd = { ...form.person, date_of_birth: v };
              if (age) { upd.approximate_age = age; upd.is_minor = Number(age) < 18; }
              setForm({ ...form, person: upd });
            }} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Edad aproximada (años)" testid="rf-age" type="number" value={form.person.approximate_age}
              onChange={(v) => {
                const upd = { ...form.person, approximate_age: v };
                const n = Number(v);
                if (v !== "" && !isNaN(n)) upd.is_minor = n <= 17;
                setForm({ ...form, person: upd });
              }} />
            <label className="block">
              <span className="text-sm font-medium text-[#475569]">Género</span>
              <select data-testid="rf-sex" value={form.person.sex} onChange={(e) => setPerson("sex", e.target.value)}
                className="mt-1 w-full h-12 px-3 rounded-lg border border-[#E2E8F0] bg-white">
                <option value="">Selecciona</option>
                {SEX_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
          </div>
          <p className="text-xs text-[#9A3412] bg-[#FFEDD5] rounded-lg p-2" data-testid="rf-minor-note">
            Si la persona tiene menos de 18 años, aplicaremos protección especial automáticamente.
          </p>
          <div>
            <Input label="C.I." testid="rf-cedula" value={form.person.venezuelan_id_number} onChange={(v) => setPerson("venezuelan_id_number", v)} />
            <p className="text-xs text-[#94A3B8] mt-1">Opcional, privado. No se mostrará públicamente.</p>
          </div>
          <Input label="Nacionalidad (opcional)" testid="rf-nationality" value={form.person.nationality} onChange={(v) => setPerson("nationality", v)} />
          <Textarea label="Descripción física" testid="rf-physical" value={form.person.physical_description} onChange={(v) => setPerson("physical_description", v)} />
          {isMissing && (
            <Textarea label="Ropa al momento de desaparecer" testid="rf-clothing" value={form.person.clothing_description} onChange={(v) => setPerson("clothing_description", v)} />
          )}
          <Textarea label="Señales particulares" testid="rf-marks" value={form.person.distinguishing_marks} onChange={(v) => setPerson("distinguishing_marks", v)} />
          {isMissing && (
            <Textarea label="Datos médicos relevantes (privados, opcional)" testid="rf-medical" value={form.person.medical_notes_private} onChange={(v) => setPerson("medical_notes_private", v)} />
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="report-step-2">
          <h2 className="font-heading text-lg font-semibold">Tomar o subir foto {isMissing ? "" : "(opcional, recomendada)"}</h2>

          {!isMissing && (
            <div className="flex gap-3 p-4 rounded-xl bg-[#FEF3C7] border border-[#FDE68A]" data-testid="found-photo-warning">
              <ShieldCheck className="w-5 h-5 text-[#92400E] shrink-0 mt-0.5" />
              <p className="text-sm text-[#92400E]">Si tomar una foto puede poner en riesgo a la persona o al reportante, continúa sin foto y describe a la persona.</p>
            </div>
          )}

          <PhotoUploader photoId={form.primary_photo_id}
            onUploaded={(id) => setForm({ ...form, primary_photo_id: id })} />

          <p className="text-xs text-[#94A3B8] leading-relaxed">
            No se aceptan ni publican fotos gráficas, de documentos de identidad, humillantes, con heridas graves visibles ni de personas fallecidas. Para menores se aplica protección especial y publicación restringida.
          </p>

          {!form.primary_photo_id ? (
            <p className="text-sm text-[#475569]" data-testid="continue-without-photo-hint">
              No hay foto. Puedes usar <b>“Continuar”</b> abajo para seguir sin foto.
            </p>
          ) : (
            <div className="space-y-2 pt-2 border-t border-[#E2E8F0]">
              <p className="text-sm font-medium text-[#475569]">Confirma sobre la foto (obligatorio para publicarla):</p>
              {isMissing && <Check testid="rf-photo-corresponds" checked={form.photo_ok.corresponds} onChange={(v) => setPhotoOk("corresponds", v)} label="La foto corresponde a la persona reportada." />}
              <Check testid="rf-photo-respectful" checked={form.photo_ok.respectful} onChange={(v) => setPhotoOk("respectful", v)} label="La foto es respetuosa." />
              <Check testid="rf-photo-notgraphic" checked={form.photo_ok.not_graphic} onChange={(v) => setPhotoOk("not_graphic", v)} label="La foto no es gráfica." />
              {isMissing && <Check testid="rf-photo-notdoc" checked={form.photo_ok.not_document} onChange={(v) => setPhotoOk("not_document", v)} label="La foto no es de un documento de identidad." />}
              <Check testid="rf-photo-notdeceased" checked={form.photo_ok.not_deceased} onChange={(v) => setPhotoOk("not_deceased", v)} label="La foto no muestra una persona fallecida." />
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4" data-testid="report-step-3">
          <h2 className="font-heading text-lg font-semibold">{isMissing ? "Última ubicación conocida" : "Zona donde fue encontrada o vista"}</h2>
          <Input label="País" testid="rf-country" value={form.location.country} onChange={(v) => setLoc("country", v)} />
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-[#475569]">Estado/región</span>
              <select data-testid="rf-state" value={form.location.state_region} onChange={(e) => setLoc("state_region", e.target.value)}
                className="mt-1 w-full h-12 px-3 rounded-lg border border-[#E2E8F0] bg-white">
                <option value="">Selecciona</option>
                {VENEZUELA_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <Input label="Municipio" testid="rf-municipality" value={form.location.municipality} onChange={(v) => setLoc("municipality", v)} />
          </div>
          <Input label="Parroquia / zona aproximada" testid="rf-parish" value={form.location.parish_zone} onChange={(v) => setLoc("parish_zone", v)} />
          {!isMissing && (
            <label className="block">
              <span className="text-sm font-medium text-[#475569]">Tipo de lugar</span>
              <select data-testid="rf-placetype" value={form.location.location_type} onChange={(e) => setLoc("location_type", e.target.value)}
                className="mt-1 w-full h-12 px-3 rounded-lg border border-[#E2E8F0] bg-white">
                <option value="">Selecciona</option>
                {PLACE_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>
          )}
          <Textarea label="Descripción de ubicación (referencia pública)" testid="rf-area" value={form.location.approximate_area_text} onChange={(v) => setLoc("approximate_area_text", v)} />
          <Input label="Dirección exacta (opcional, privada)" testid="rf-exact" value={form.location.exact_address_private} onChange={(v) => setLoc("exact_address_private", v)} />
          <DateField label={isMissing ? "Fecha aproximada de desaparición" : "Fecha aproximada en que fue encontrada/vista"}
            testid="rf-date" max={new Date().toISOString().slice(0, 10)}
            value={form.approximate_date} onChange={(v) => setForm({ ...form, approximate_date: v })} />
          <p className="text-xs text-[#94A3B8] flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Solo se publica la zona aproximada. La dirección exacta permanece privada.</p>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4" data-testid="report-step-4">
          <h2 className="font-heading text-lg font-semibold">Tus datos (privados)</h2>
          <div className="flex gap-3 p-3 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE]">
            <ShieldCheck className="w-5 h-5 text-[#1E3A8A] shrink-0" />
            <p className="text-sm text-[#1E40AF]">Tus datos de contacto nunca se mostrarán públicamente.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Nombre *" testid="rf-rep-firstname" value={form.reporter.first_name} onChange={(v) => setRep("first_name", v)} required />
            <Input label="Apellido" testid="rf-rep-lastname" value={form.reporter.last_name} onChange={(v) => setRep("last_name", v)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Teléfono" testid="rf-rep-phone" value={form.reporter.phone} onChange={(v) => setRep("phone", v)} />
            <Input label="Email" testid="rf-rep-email" type="email" value={form.reporter.email} onChange={(v) => setRep("email", v)} />
          </div>
          <label className="block">
            <span className="text-sm font-medium text-[#475569]">Método de contacto preferido</span>
            <select data-testid="rf-rep-method" value={form.reporter.preferred_contact_method} onChange={(e) => setRep("preferred_contact_method", e.target.value)}
              className="mt-1 w-full h-12 px-3 rounded-lg border border-[#E2E8F0] bg-white">
              {CONTACT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-[#475569]">¿Por qué estás reportando a esta persona?</span>
            <select data-testid="rf-rep-reason" value={form.reporter.report_reason} onChange={(e) => setRep("report_reason", e.target.value)}
              className="mt-1 w-full h-12 px-3 rounded-lg border border-[#E2E8F0] bg-white">
              <option value="">Selecciona</option>
              {REPORT_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          <Input label="Relación con la persona" testid="rf-rep-relationship" value={form.reporter.relationship_to_person} onChange={(v) => setRep("relationship_to_person", v)} />
          <Textarea label="Mensaje privado (opcional)" testid="rf-rep-message" value={form.reporter.message_private} onChange={(v) => setRep("message_private", v)} />
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4" data-testid="report-step-5">
          <h2 className="font-heading text-lg font-semibold">Confirmación</h2>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-2 text-sm">
            <Row label="Nombre" value={[form.person.first_name, form.person.last_name].filter(Boolean).join(" ") || "—"} />
            <Row label="Edad aprox." value={form.person.approximate_age || "—"} />
            <Row label="Zona" value={[form.location.municipality, form.location.state_region].filter(Boolean).join(", ") || "—"} />
            <Row label="Foto" value={form.primary_photo_id ? "Sí" : "No"} />
            <Row label="Menor" value={form.person.is_minor ? "Sí (protección especial)" : "No"} />
          </div>
          <div className="flex gap-3 p-4 rounded-xl bg-[#FEF3C7] border border-[#FDE68A]">
            <ShieldCheck className="w-5 h-5 text-[#92400E] shrink-0 mt-0.5" />
            <p className="text-sm text-[#92400E]">Tu información de contacto no será mostrada públicamente. La ficha pública solo mostrará datos limitados para ayudar a identificar a la persona.</p>
          </div>
          <Check testid="rf-goodfaith" checked={form.good_faith_confirmation} onChange={(v) => setForm({ ...form, good_faith_confirmation: v })} label="Confirmo que este reporte es de buena fe y la información es verdadera." />
          <Check testid="rf-terms" checked={form.terms_accepted} onChange={(v) => setForm({ ...form, terms_accepted: v })} label="Acepto los términos de uso." />
          <Check testid="rf-privacy" checked={form.privacy_accepted} onChange={(v) => setForm({ ...form, privacy_accepted: v })} label="Acepto la política de privacidad." />
        </div>
      )}

      {error && <p className="text-sm text-[#DC2626]" data-testid="report-error">{error}</p>}

      <div className="flex gap-3 pt-2">
        {step > 1 && (
          <button onClick={back} data-testid="report-back-btn"
            className="flex-1 h-14 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-semibold flex items-center justify-center gap-1">
            <ChevronLeft className="w-5 h-5" /> Atrás
          </button>
        )}
        {step < totalSteps ? (
          <button onClick={next} data-testid="report-next-btn"
            className="flex-1 h-14 rounded-xl bg-[#1E3A8A] text-white font-semibold flex items-center justify-center gap-1">
            Continuar <ChevronRight className="w-5 h-5" />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting} data-testid="report-submit-btn"
            className="flex-1 h-14 rounded-xl bg-[#1E3A8A] text-white font-semibold disabled:opacity-50">
            {submitting ? "Enviando…" : "Enviar reporte"}
          </button>
        )}
      </div>
      <button onClick={() => attempt(() => navigate("/"))} data-testid="report-cancel-btn"
        className="w-full h-11 rounded-xl border border-[#E2E8F0] text-[#475569] text-sm font-medium hover:bg-[#F1F5F9]">
        Cancelar
      </button>

      {dupMatches && (
        <DuplicateModal matches={dupMatches} navigate={navigate}
          onContinue={() => runCreate(dupMatches[0])}
          onBack={() => setDupMatches(null)} submitting={submitting} />
      )}
    </div>
  );
}

function DuplicateModal({ matches, navigate, onContinue, onBack, submitting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 overflow-y-auto" data-testid="duplicate-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onBack} />
      <div className="relative bg-white rounded-2xl p-5 max-w-lg w-full my-6 space-y-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-6 h-6 text-[#D97706] shrink-0" />
          <div>
            <h3 className="font-heading text-lg font-bold text-[#0F172A]">Posibles coincidencias encontradas</h3>
            <p className="text-sm text-[#475569]">Encontramos reportes que podrían corresponder a la misma persona. Revisa antes de continuar.</p>
          </div>
        </div>

        <div className="space-y-2 max-h-[44vh] overflow-y-auto">
          {matches.map((m) => (
            <div key={m.id} className="border border-[#E2E8F0] rounded-xl p-3 flex gap-3" data-testid={`dup-match-${m.public_case_code}`}>
              <div className="w-14 h-14 rounded-lg bg-[#F1F5F9] overflow-hidden shrink-0 flex items-center justify-center">
                {m.photo_url
                  ? <img src={photoSrc(m.photo_url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  : <span className="text-[10px] text-[#94A3B8]">Sin foto</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1E40AF]">{m.case_type_label}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${m.match_level === "strong" ? "bg-[#FEE2E2] text-[#991B1B]" : m.match_level === "medium" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#F1F5F9] text-[#475569]"}`}>
                    {m.match_level === "strong" ? "Coincidencia fuerte" : m.match_level === "medium" ? "Coincidencia media" : "Coincidencia baja"}
                  </span>
                </div>
                <p className="text-sm font-medium mt-1 truncate">{m.first_name || m.nickname || "Sin nombre"}{m.approximate_age ? ` · ${m.approximate_age} años` : ""}</p>
                <p className="text-xs text-[#64748B] truncate">{m.approximate_zone || "Zona no especificada"} · <span className="font-mono">{m.public_case_code}</span> · {m.status_label}</p>
                <p className="text-xs text-[#94A3B8] truncate">Motivo: {m.match_reason}</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => navigate(`/caso/${m.public_case_code}`)} data-testid={`dup-view-${m.public_case_code}`}
                    className="text-xs font-medium text-[#1E3A8A] underline">Ver ficha (es la misma persona)</button>
                  <button onClick={() => navigate(`/caso/${m.public_case_code}/contacto?tipo=have_information`)} data-testid={`dup-addinfo-${m.public_case_code}`}
                    className="text-xs font-medium text-[#047857] underline">Agregar información a este caso</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <button onClick={onBack} disabled={submitting} data-testid="dup-back-btn"
            className="flex-1 h-12 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-semibold disabled:opacity-50">Volver y editar datos</button>
          <button onClick={onContinue} disabled={submitting} data-testid="dup-continue-btn"
            className="flex-1 h-12 rounded-xl bg-[#1E3A8A] text-white font-semibold disabled:opacity-50">
            {submitting ? "Creando…" : "Continuar como nuevo reporte"}
          </button>
        </div>
        <p className="text-xs text-[#94A3B8]">Solo se muestran datos públicos limitados. Los datos privados y de contacto nunca se exponen.</p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[#94A3B8]">{label}</span>
      <span className="text-[#0F172A] font-medium text-right">{value}</span>
    </div>
  );
}
